import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
    Box,
    Button,
    Collapse,
    Link,
    SxProps,
    Theme,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';

interface DeviceSetupInstructionsProps {
    sx?: SxProps<Theme>;
}

const DeviceSetupInstructions = ({ sx }: DeviceSetupInstructionsProps) => {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                ...sx,
            }}
        >
            <Button
                size='small'
                onClick={() => setOpen((v) => !v)}
                endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                    mb: 1,
                    px: 0,
                    minWidth: 0,
                    alignSelf: 'flex-start',
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'background.default' },
                }}
            >
                {open ? 'Hide setup instructions' : 'Show setup instructions'}
            </Button>
            <Collapse in={open}>
                <Typography
                    color='text.secondary'
                    sx={{ fontSize: '12px', mb: 1.5 }}
                >
                    Follow these steps to configure your{' '}
                    <Link
                        href='https://www.amazon.com.au/SinoTrack-ST-915L-Waterproof-Real-Time-Motorcycle/dp/B09K3SKM9F'
                        target='_blank'
                        rel='noopener'
                    >
                        SinoTrack ST-915L
                    </Link>{' '}
                    GPS tracker. This is our recommended device for live
                    tracking.
                </Typography>
                <Box component='ol' sx={{ pl: 2.5, m: 0, '& li': { mb: 1 } }}>
                    <li>
                        <Typography sx={{ fontSize: '12px' }}>
                            Insert a SIM card with <strong>SMS support</strong>{' '}
                            into the device.
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ fontSize: '12px' }}>
                            Power on the device and wait for it to become
                            active.
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ fontSize: '12px' }}>
                            Send the following SMS to the SIM card's phone
                            number:
                        </Typography>
                        <Box
                            component='code'
                            sx={{
                                display: 'block',
                                mt: 0.5,
                                p: 1,
                                borderRadius: 1,
                                bgcolor: 'action.hover',
                                fontSize: '13px',
                                fontFamily: 'monospace',
                                width: 'fit-content',
                            }}
                        >
                            8040000 78.47.48.251 5013
                        </Box>
                        <Typography
                            variant='body3'
                            color='text.secondary'
                            sx={{ mt: 0.5 }}
                        >
                            The device should respond with{' '}
                            <strong>SET OK</strong>.
                        </Typography>
                    </li>
                    <li>
                        <Typography sx={{ fontSize: '12px' }}>
                            Send{' '}
                            <Box
                                component='code'
                                sx={{
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: 0.5,
                                    bgcolor: 'action.hover',
                                    fontSize: '13px',
                                    fontFamily: 'monospace',
                                }}
                            >
                                RCONF
                            </Box>{' '}
                            via SMS to the same number and confirm the IP
                            address in the response matches{' '}
                            <strong>78.47.48.251</strong>.
                        </Typography>
                    </li>
                </Box>
                <Typography
                    color='text.secondary'
                    sx={{ mt: 2, fontStyle: 'italic', fontSize: '12px' }}
                >
                    Need help? Contact our support team at{' '}
                    <Link href='mailto:support@7-day.com'>
                        support@7-day.com
                    </Link>{' '}
                    and we can set the device up for you. Just have your SIM
                    card number ready.
                </Typography>
            </Collapse>
        </Box>
    );
};

export default DeviceSetupInstructions;
